import React, { useCallback, useEffect, useState } from "react";
import { ToastProps, ToastInstance, ToastReturnType, ToastType } from "./PropsTypes";
import BaseToast from "./Toast";
import { canUseDom, render, resolveContainer, unmount } from "@/utils/components-utils";

const extend = Object.assign;

function isObject(val: unknown): val is Record<any, any> {
	return val !== null && typeof val === "object";
}

const defaultOptions: ToastProps = {
	message: "",
	type: "info",
	duration: 2500,
};

const toastArray: (() => void)[] = [];
let allowMultiple = false;
let currentOptions = extend({}, defaultOptions);

const defaultOptionsMap = new Map<string, ToastProps>();

// 同步的销毁
function syncClear() {
	let fn = toastArray.pop();
	while (fn) {
		fn();
		fn = toastArray.pop();
	}
}

// 针对 toast 还没弹出来就立刻销毁的情况，将销毁放到下一个 event loop 中，避免销毁失败。
function nextTickClear() {
	setTimeout(syncClear);
}

// 可返回用于销毁此弹窗的方法
const ToastObj = (p: ToastProps): unknown => {
	if (!canUseDom) return null;
	const props = parseOptions(p);
	const update: ToastReturnType = {
		config: () => {},
		clear: () => null,
	};
	let timer = 0;
	const { onClose, teleport } = props;
	const container = document.createElement("div");
	const bodyContainer = resolveContainer(teleport);
	bodyContainer.appendChild(container);

	const TempToast = () => {
		const options = {
			duration: 2500,
			...props,
		} as ToastProps;

		const [visible, setVisible] = useState(false);
		const [state, setState] = useState<ToastProps>({ ...options });
		// clearDOM after animation
		const internalOnClosed = useCallback(() => {
			setVisible(false);
			window.setTimeout(
				() => {
					const unmountResult = unmount(container);
					if (unmountResult && container.parentNode) {
						container.parentNode.removeChild(container);
					}
				},
				+(state as any).duration || +(defaultOptions as any).duration,
			);

			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [container]);

		// close with animation
		const destroy = useCallback(() => {
			setVisible(false);
			if (onClose) onClose();
		}, []);

		update.clear = internalOnClosed;

		update.config = useCallback(
			(nextState) => {
				setState((prev) =>
					typeof nextState === "function" ? { ...prev, ...nextState(prev) } : { ...prev, ...nextState },
				);
			},
			[setState],
		);

		useEffect(() => {
			setVisible(true);
			if (!allowMultiple) syncClear();
			toastArray.push(internalOnClosed);

			if (state.duration !== 0 && "duration" in state) {
				timer = window.setTimeout(destroy, +(state as any).duration);
			}
			return () => {
				if (timer !== 0) {
					window.clearTimeout(timer);
				}
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return <BaseToast {...state} visible={visible} />;
	};

	render(<TempToast />, container);

	return update;
};

function parseOptions(message: any) {
	if (isObject(message)) {
		return message;
	}
	return { message };
}

const createMethod = (type: ToastType) => (options: ToastProps) =>
	ToastObj({
		...currentOptions,
		...defaultOptionsMap.get(type),
		...parseOptions(options),
		type,
	});

const toastTypes: ToastType[] = ["info", "pending", "success", "fail"];
toastTypes.forEach((method) => {
	(ToastObj as any)[method] = createMethod(method);
});

ToastObj.allowMultiple = (value = true) => {
	allowMultiple = value;
};
ToastObj.clear = nextTickClear;

function setDefaultOptions(type: ToastType | ToastProps, options?: ToastProps) {
	if (typeof type === "string" && options !== undefined) {
		defaultOptionsMap.set(type, options);
	} else {
		extend(currentOptions, type);
	}
}

ToastObj.setDefaultOptions = setDefaultOptions;

ToastObj.resetDefaultOptions = (type?: ToastType) => {
	if (typeof type === "string") {
		defaultOptionsMap.delete(type);
	} else {
		currentOptions = extend({}, defaultOptions);
		defaultOptionsMap.clear();
	}
};

const Toast = ToastObj as ToastInstance;
export default Toast as ToastInstance;
